/**
 * Mobile PDP gallery: segmented progress bar synced with the active slide.
 * Supports swipe (scroll) + tap/keyboard on segments.
 */
(function () {
  function getAllSlides(slider) {
    return Array.from(slider.querySelectorAll('.slider__slide'));
  }

  function activeIndexFromScroll(slider) {
    const slides = getAllSlides(slider);
    if (!slides.length) return 0;
    const step = slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : slides[0].clientWidth;
    if (!step) return activeIndexFromActiveClass(slider);
    return Math.min(slides.length - 1, Math.max(0, Math.round(slider.scrollLeft / step)));
  }

  function activeIndexFromActiveClass(slider) {
    const slides = getAllSlides(slider);
    const idx = slides.findIndex((el) => el.classList.contains('is-active'));
    return idx >= 0 ? idx : 0;
  }

  function resolveActiveIndex(slider) {
    const slides = getAllSlides(slider);
    if (slides.length <= 1) return 0;
    // Prefer scroll position when the carousel is laid out horizontally
    if (slider.scrollWidth > slider.clientWidth + 2) return activeIndexFromScroll(slider);
    return activeIndexFromActiveClass(slider);
  }

  function setSlideActiveClasses(slider, index) {
    getAllSlides(slider).forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });
  }

  function scrollToSlide(slider, index) {
    const slides = getAllSlides(slider);
    const slide = slides[index];
    if (!slide) return;
    slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  function initGalleryProgress(mediaGallery) {
    if (mediaGallery.dataset.galleryProgressInit === '1') return;
    mediaGallery.dataset.galleryProgressInit = '1';

    const progress = mediaGallery.querySelector('[data-gallery-progress]');
    const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (!progress || !viewer) return;

    const slider = viewer.querySelector('[id^="Slider-Gallery"]');
    if (!slider) return;

    let segments = [];
    let syncingFromClick = false;

    function setActive(index) {
      if (!segments.length) return;
      const i = Math.max(0, Math.min(segments.length - 1, index));
      segments.forEach((seg, j) => {
        const active = j === i;
        seg.classList.toggle('is-active', active);
        seg.setAttribute('aria-current', active ? 'true' : 'false');
      });
      setSlideActiveClasses(slider, i);
    }

    function syncActiveIndex() {
      if (syncingFromClick) return;
      setActive(resolveActiveIndex(slider));
    }

    function goToIndex(index) {
      const slides = getAllSlides(slider);
      const i = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[i];
      if (!slide) return;

      syncingFromClick = true;
      setActive(i);

      const mediaId = slide.dataset.mediaId;
      if (mediaId && typeof mediaGallery.setActiveMedia === 'function') {
        mediaGallery.setActiveMedia(mediaId, false);
      } else {
        scrollToSlide(slider, i);
      }

      // Ensure horizontal scroll even if setActiveMedia no-ops on layout
      window.requestAnimationFrame(() => {
        scrollToSlide(slider, i);
        window.setTimeout(() => {
          syncingFromClick = false;
          syncActiveIndex();
        }, 350);
      });
    }

    function rebuildSegments() {
      const slides = getAllSlides(slider);
      progress.innerHTML = '';
      segments = [];
      if (slides.length < 2) {
        progress.hidden = true;
        return;
      }
      progress.hidden = false;

      slides.forEach((slide, i) => {
        const seg = document.createElement('button');
        seg.type = 'button';
        seg.className = 'product-gallery__progress-segment';
        if (i === resolveActiveIndex(slider)) seg.classList.add('is-active');
        seg.setAttribute('aria-label', `View image ${i + 1}`);
        seg.setAttribute('aria-current', i === resolveActiveIndex(slider) ? 'true' : 'false');

        seg.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          goToIndex(i);
        });

        progress.appendChild(seg);
        segments.push(seg);
      });
    }

    rebuildSegments();
    syncActiveIndex();

    viewer.addEventListener('slideChanged', () => {
      window.requestAnimationFrame(syncActiveIndex);
    });

    slider.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(syncActiveIndex);
      },
      { passive: true }
    );

    const mutationObserver = new MutationObserver(() => window.requestAnimationFrame(syncActiveIndex));
    mutationObserver.observe(slider, { subtree: true, attributes: true, attributeFilter: ['class'] });

    const resizeObserver = new ResizeObserver(() => {
      rebuildSegments();
      syncActiveIndex();
    });
    resizeObserver.observe(slider);
  }

  function initAll() {
    document.querySelectorAll('media-gallery[data-section-id]').forEach(initGalleryProgress);
  }

  document.addEventListener('shopify:section:load', initAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
